import { useState, useEffect, useCallback, useMemo } from "react";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as zip from "@zip.js/zip.js";
import {
  BondRefundRow,
  CustomerInfo,
  HuulgaQuarter,
  OrlogoZarlagaRow,
  HudaldanAvaltRow,
  HUULGA_QUARTERS,
  HuulgaQuarterFileTypeKey,
  BuitsaaltRow,
  QuarterHudaldanAvaltRow,
  QuarterSearchResult,
  StockRefundRow,
  TsenegleltRow,
  YearData,
  YearFileInfo,
  YearQuarterData,
  YearQuarterFileInfo,
  SearchResult,
} from "./types";
import {
  parseBondRefundCSV,
  parseHaNiitCSV,
  parseNiitHudaldanAvaltCSV,
  parseNiitTsenegleltCSV,
  parseStockRefundCSV,
  parseTseneglelCSV,
  parseZarlagaCSV,
  parseBuitsaaltCSV,
  formatMoney,
} from "./parsers";

const HUULGA_API = import.meta.env.VITE_HUULGA_API_URL || "https://huulga-backend.vercel.app/api";
const HUULGA_API_KEY = import.meta.env.VITE_HUULGA_API_KEY || "avtamata-huulga-secret-2024";

type FileTypeKey = "haNiitText" | "tseneglelText" | "zarlagaText" | "buitsaaltText";
type QuarterFileTypeKey = HuulgaQuarterFileTypeKey;

const FILE_TYPE_MAP: Record<FileTypeKey, string> = {
  haNiitText: "haNiit",
  tseneglelText: "tsenegel",
  zarlagaText: "zarlaga",
  buitsaaltText: "buitsaalt",
};

const QUARTER_FILE_TYPE_MAP: Record<
  QuarterFileTypeKey,
  string
> = {
  niitHudaldanAvaltText: "niitHudaldanAvalt",
  niitTsenegleltText: "niitTseneglelt",
  buitsaaltBondText: "buitsaaltBond",
  buitsaaltStockText: "buitsaaltStock",
};

const API_TYPE_TO_FIELD: Record<string, FileTypeKey> = {
  haNiit: "haNiitText",
  tsenegel: "tseneglelText",
  zarlaga: "zarlagaText",
  buitsaalt: "buitsaaltText",
};

const API_TYPE_TO_QUARTER_FIELD: Record<
  string,
  QuarterFileTypeKey
> = {
  niitHudaldanAvalt: "niitHudaldanAvaltText",
  niitTseneglelt: "niitTsenegleltText",
  buitsaaltBond: "buitsaaltBondText",
  buitsaaltStock: "buitsaaltStockText",
};

class HuulgaApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function parseApiResponse(res: Response) {
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }

  const text = await res.text();
  return text;
}

async function apiFetch(path: string, options?: RequestInit) {
  const res = await fetch(`${HUULGA_API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${HUULGA_API_KEY}`,
      ...options?.headers,
    },
  });

  const payload = await parseApiResponse(res);
  if (!res.ok) {
    const message =
      typeof payload === "string"
        ? payload
        : payload?.message || payload?.error || `Request failed: ${res.status}`;
    throw new HuulgaApiError(res.status, message);
  }

  return payload;
}

async function apiDelete(path: string, payload: unknown) {
  const res = await fetch(`${HUULGA_API}${path}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${HUULGA_API_KEY}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Delete failed: ${res.status}`);
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }
  return null;
}

const PLAIN_UPLOAD_MAX_BYTES = 256 * 1024;
const CHUNK_UPLOAD_MAX_BYTES = 512 * 1024;

function getUtf8ByteLength(text: string) {
  return new TextEncoder().encode(text).byteLength;
}

function splitTextIntoChunks(
  text: string,
  maxBytes: number
): string[] {
  const lines = text.split("\n");
  const chunks: string[] = [];
  let currentChunk = "";
  let currentBytes = 0;

  for (let index = 0; index < lines.length; index++) {
    const line =
      index < lines.length - 1
        ? `${lines[index]}\n`
        : lines[index];
    const lineBytes = getUtf8ByteLength(line);

    if (currentChunk && currentBytes + lineBytes > maxBytes) {
      chunks.push(currentChunk);
      currentChunk = line;
      currentBytes = lineBytes;
      continue;
    }

    if (!currentChunk && lineBytes > maxBytes) {
      chunks.push(line);
      currentChunk = "";
      currentBytes = 0;
      continue;
    }

    currentChunk += line;
    currentBytes += lineBytes;
  }

  if (currentChunk) {
    chunks.push(currentChunk);
  }

  return chunks;
}

async function uploadChunkedCsv(params: {
  year: number;
  quarter?: HuulgaQuarter;
  fileType: string;
  fileName: string;
  csvText: string;
  onProgress?: (statusText: string) => void;
}) {
  const {
    year,
    quarter,
    fileType,
    fileName,
    csvText,
    onProgress,
  } = params;

  const chunks = splitTextIntoChunks(
    csvText,
    CHUNK_UPLOAD_MAX_BYTES
  );

  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    onProgress?.(`Uploading ${chunkIndex + 1}/${chunks.length}`);
    await apiFetch("/upload", {
      method: "POST",
      body: JSON.stringify({
        year,
        quarter,
        fileType,
        fileName,
        uploadMode: "chunked",
        chunkIndex,
        chunkCount: chunks.length,
        csvChunk: chunks[chunkIndex],
      }),
    });
  }
}

type ZipTextEntry = zip.Entry & {
  getData: (writer: zip.TextWriter) => Promise<string>;
};

const hasZipTextData = (entry: zip.Entry): entry is ZipTextEntry =>
  typeof (entry as ZipTextEntry).getData === "function";

function isExcelFileName(fileName: string) {
  const lower = fileName.toLowerCase();
  return lower.endsWith(".xlsx") || lower.endsWith(".xls");
}

function isXlsxFileName(fileName: string) {
  return fileName.toLowerCase().endsWith(".xlsx");
}

function escapeCsvValue(value: string) {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function columnLettersToIndex(columnLetters: string) {
  let result = 0;
  for (let i = 0; i < columnLetters.length; i++) {
    result = result * 26 + (columnLetters.charCodeAt(i) - 64);
  }
  return result - 1;
}

function excelSerialToDate(serial: number) {
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const fractionalDay = serial - Math.floor(serial) + 0.0000001;
  const totalSeconds = Math.floor(fractionalDay * 86400);
  const date = new Date(utcValue * 1000);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCSeconds(totalSeconds);
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
}

function formatDateForCsv(date: Date) {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  if (hh === "00" && mm === "00" && ss === "00") {
    return `${y}/${m}/${d}`;
  }
  return `${y}/${m}/${d} ${hh}:${mm}:${ss}`;
}

function isDateNumberFormat(formatCode: string) {
  const normalized = formatCode.toLowerCase();
  if (!/[ymdhs]/.test(normalized)) return false;
  if (/\[h\]|\[m\]|\[s\]/.test(normalized)) return true;
  return /y|m|d|h|s/.test(normalized);
}

async function readZipEntryText(
  entriesByName: Map<string, zip.Entry>,
  fileName: string
) {
  const entry = entriesByName.get(fileName);
  if (!entry || !hasZipTextData(entry)) return "";
  return entry.getData(new zip.TextWriter());
}

function normalizeZipPath(basePath: string, target: string) {
  const combined = target.startsWith("/")
    ? target.slice(1)
    : `${basePath}/${target}`;
  const parts = combined.split("/");
  const resolved: string[] = [];
  for (const part of parts) {
    if (!part || part === ".") continue;
    if (part === "..") {
      resolved.pop();
      continue;
    }
    resolved.push(part);
  }
  return resolved.join("/");
}

async function excelFileToCSV(file: File): Promise<string> {
  const fileReader = new zip.BlobReader(file);
  const zipReader = new zip.ZipReader(fileReader);
  try {
    const entries = await zipReader.getEntries();
    const entriesByName = new Map(entries.map((entry) => [entry.filename, entry]));

    const workbookXml = await readZipEntryText(entriesByName, "xl/workbook.xml");
    const workbookRelsXml = await readZipEntryText(
      entriesByName,
      "xl/_rels/workbook.xml.rels"
    );
    if (!workbookXml || !workbookRelsXml) {
      throw new Error("Excel workbook бүтэц олдсонгүй");
    }

    const workbookDoc = new DOMParser().parseFromString(workbookXml, "application/xml");
    const workbookRelsDoc = new DOMParser().parseFromString(
      workbookRelsXml,
      "application/xml"
    );
    const firstSheet = workbookDoc.getElementsByTagName("sheet")[0];
    if (!firstSheet) return "";

    const relId =
      firstSheet.getAttribute("r:id") ||
      firstSheet.getAttributeNS(
        "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
        "id"
      );
    if (!relId) {
      throw new Error("Excel sheet холбоос олдсонгүй");
    }

    const relationships = Array.from(workbookRelsDoc.getElementsByTagName("Relationship"));
    const matchingRel = relationships.find((rel) => rel.getAttribute("Id") === relId);
    if (!matchingRel) {
      throw new Error("Excel sheet файл олдсонгүй");
    }

    const sheetTarget = matchingRel.getAttribute("Target");
    if (!sheetTarget) {
      throw new Error("Excel sheet target хоосон байна");
    }

    const sheetPath = normalizeZipPath("xl", sheetTarget);
    const sheetXml = await readZipEntryText(entriesByName, sheetPath);
    if (!sheetXml) return "";

    const sharedStringsXml = await readZipEntryText(entriesByName, "xl/sharedStrings.xml");
    const stylesXml = await readZipEntryText(entriesByName, "xl/styles.xml");

    const sharedStrings = sharedStringsXml
      ? Array.from(
          new DOMParser()
            .parseFromString(sharedStringsXml, "application/xml")
            .getElementsByTagName("si")
        ).map((si) => si.textContent || "")
      : [];

    const dateStyleIndexes = new Set<number>();
    if (stylesXml) {
      const stylesDoc = new DOMParser().parseFromString(stylesXml, "application/xml");
      const customNumFmtMap = new Map<number, string>();
      Array.from(stylesDoc.getElementsByTagName("numFmt")).forEach((numFmt) => {
        const id = Number(numFmt.getAttribute("numFmtId"));
        const formatCode = numFmt.getAttribute("formatCode") || "";
        if (!Number.isNaN(id)) {
          customNumFmtMap.set(id, formatCode);
        }
      });

      const builtInDateFormats = new Set([
        14, 15, 16, 17, 18, 19, 20, 21, 22, 27, 30, 36, 45, 46, 47, 50, 57,
      ]);

      const cellXfs = Array.from(stylesDoc.getElementsByTagName("cellXfs")[0]?.getElementsByTagName("xf") || []);
      cellXfs.forEach((xf, index) => {
        const numFmtId = Number(xf.getAttribute("numFmtId"));
        if (builtInDateFormats.has(numFmtId)) {
          dateStyleIndexes.add(index);
          return;
        }

        const customFormat = customNumFmtMap.get(numFmtId);
        if (customFormat && isDateNumberFormat(customFormat)) {
          dateStyleIndexes.add(index);
        }
      });
    }

    const sheetDoc = new DOMParser().parseFromString(sheetXml, "application/xml");
    const rowElements = Array.from(sheetDoc.getElementsByTagName("row"));
    const csvRows: string[] = [];

    rowElements.forEach((rowElement) => {
      const values: string[] = [];
      const cellElements = Array.from(rowElement.getElementsByTagName("c"));

      cellElements.forEach((cell) => {
        const ref = cell.getAttribute("r") || "";
        const columnMatch = ref.match(/[A-Z]+/);
        const columnIndex = columnMatch
          ? columnLettersToIndex(columnMatch[0])
          : values.length;

        while (values.length < columnIndex) {
          values.push("");
        }

        const cellType = cell.getAttribute("t") || "";
        const styleIndex = Number(cell.getAttribute("s"));
        const valueNode = cell.getElementsByTagName("v")[0];
        const inlineTextNode = cell.getElementsByTagName("is")[0];
        let cellValue = "";

        if (cellType === "s" && valueNode) {
          cellValue = sharedStrings[Number(valueNode.textContent || "0")] || "";
        } else if (cellType === "inlineStr" && inlineTextNode) {
          cellValue = inlineTextNode.textContent || "";
        } else if (cellType === "b" && valueNode) {
          cellValue = valueNode.textContent === "1" ? "TRUE" : "FALSE";
        } else if (valueNode) {
          const rawValue = valueNode.textContent || "";
          const numericValue = Number(rawValue);
          if (
            rawValue &&
            !Number.isNaN(numericValue) &&
            dateStyleIndexes.has(styleIndex)
          ) {
            cellValue = formatDateForCsv(excelSerialToDate(numericValue));
          } else {
            cellValue = rawValue;
          }
        }

        values[columnIndex] = escapeCsvValue(cellValue);
      });

      csvRows.push(values.join(","));
    });

    return csvRows.join("\n");
  } finally {
    await zipReader.close();
  }
}

async function fileToCSV(file: File): Promise<string> {
  const isExcel = isExcelFileName(file.name);

  if (isExcel) {
    if (isXlsxFileName(file.name)) {
      return excelFileToCSV(file);
    }

    const ExcelJSLib = (await import("exceljs")).default;
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJSLib.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.worksheets[0];
    if (!ws) return "";

    const rows: string[] = [];
    ws.eachRow((row) => {
      const vals = (row.values as (string | number | Date | null)[])
        .slice(1)
        .map((v) => {
          if (v == null) return "";
          if (v instanceof Date) {
            return formatDateForCsv(v);
          }
          return escapeCsvValue(String(v));
        });
      rows.push(vals.join(","));
    });
    return rows.join("\n");
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.readAsText(file);
  });
}

async function compressTextToGzipBase64(text: string): Promise<string> {
  const stream = new CompressionStream("gzip");
  const writer = stream.writable.getWriter();
  await writer.write(new TextEncoder().encode(text));
  await writer.close();

  const compressedBuffer = await new Response(stream.readable).arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Compressed content-ийг base64 болгож чадсангүй"));
        return;
      }

      const base64 = result.split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = () => {
      reject(reader.error || new Error("Compressed content уншихад алдаа гарлаа"));
    };
    reader.readAsDataURL(new Blob([compressedBuffer], { type: "application/gzip" }));
  });
}

function searchYear(
  yearData: YearData,
  emailLower: string
): {
  customer: CustomerInfo | null;
  registerNo: string;
  orlogoZarlaga: OrlogoZarlagaRow[];
  hudaldanAvalt: HudaldanAvaltRow[];
  buitsaalt: BuitsaaltRow[];
} {
  const year = yearData.year;
  const { customers } = parseHaNiitCSV(yearData.haNiitText);
  const customerData = customers.get(emailLower);

  let customer: CustomerInfo | null = null;
  let registerNo = "";
  const orlogoZarlaga: OrlogoZarlagaRow[] = [];
  const hudaldanAvalt: HudaldanAvaltRow[] = [];

  if (customerData) {
    customer = {
      email: customerData.email,
      register: customerData.register,
      lastName: customerData.lastName,
      firstName: customerData.firstName,
      phone: customerData.phone,
    };
    registerNo = customerData.registerNo;

    // Add year to purchases
    customerData.purchases.forEach((p) => {
      hudaldanAvalt.push({ ...p, year, key: `${year}-${p.key}` });
    });

    // Орлого from Цэнэглэлт
    const tseneglelData = parseTseneglelCSV(yearData.tseneglelText);
    const deposits = tseneglelData.get(emailLower) || [];
    deposits.forEach((dep, idx) => {
      orlogoZarlaga.push({
        key: `${year}-oz-dep-${idx}`,
        year,
        date: dep.date,
        income: formatMoney(dep.amount),
        expense: "",
        description: dep.description || "Bank deposit",
        depositDate: dep.depositDate,
      });
    });

    // Зарлага from Зарлага file
    const { rows: zarlagaRows } = parseZarlagaCSV(yearData.zarlagaText);
    const matchingExpenses = zarlagaRows.filter((row) =>
      row.code.toUpperCase().includes(registerNo.toUpperCase())
    );
    matchingExpenses.forEach((row, idx) => {
      orlogoZarlaga.push({
        key: `${year}-oz-exp-${idx}`,
        year,
        date: row.date,
        income: "",
        expense: formatMoney(row.amount),
        description: row.code,
        depositDate: row.account,
      });
    });
  }

  // Буцаалт
  const buitsaaltData = parseBuitsaaltCSV(yearData.buitsaaltText);
  const buitsaaltRows = (buitsaaltData.get(emailLower) || []).map((r) => ({
    ...r,
    year,
    key: `${year}-${r.key}`,
  }));

  return { customer, registerNo, orlogoZarlaga, hudaldanAvalt, buitsaalt: buitsaaltRows };
}

function searchYearQuarter(
  yearQuarterData: YearQuarterData,
  emailLower: string
): {
  customer: CustomerInfo | null;
  hudaldanAvalt: QuarterHudaldanAvaltRow[];
  tseneglelt: TsenegleltRow[];
  bondRefunds: BondRefundRow[];
  stockRefunds: StockRefundRow[];
  holdingKeys: string[];
} {
  const { year, quarter } = yearQuarterData;
  const purchaseResult = parseNiitHudaldanAvaltCSV(
    yearQuarterData.niitHudaldanAvaltText
  );
  const customerData = purchaseResult.customers.get(
    emailLower
  );

  let customer: CustomerInfo | null = null;
  const hudaldanAvalt: QuarterHudaldanAvaltRow[] = [];

  if (customerData) {
    customer = {
      email: customerData.email,
      register: customerData.register,
      lastName: customerData.lastName,
      firstName: customerData.firstName,
      phone: customerData.phone,
    };

    customerData.purchases.forEach((purchase) => {
      hudaldanAvalt.push({
        ...purchase,
        year,
        quarter,
        key: `${year}-${quarter}-${purchase.key}`,
      });
    });
  }

  const tsenegleltRows = (
    parseNiitTsenegleltCSV(
      yearQuarterData.niitTsenegleltText
    ).get(emailLower) || []
  ).map((row) => ({
    ...row,
    year,
    quarter,
    key: `${year}-${quarter}-${row.key}`,
  }));

  const bondRefundRows = (
    parseBondRefundCSV(
      yearQuarterData.buitsaaltBondText
    ).get(emailLower) || []
  ).map((row) => ({
    ...row,
    year,
    quarter,
    key: `${year}-${quarter}-${row.key}`,
  }));

  const stockRefundRows = (
    parseStockRefundCSV(
      yearQuarterData.buitsaaltStockText
    ).get(emailLower) || []
  ).map((row) => ({
    ...row,
    year,
    quarter,
    key: `${year}-${quarter}-${row.key}`,
  }));

  if (!customer) {
    const fallbackRow =
      tsenegleltRows[0] ||
      bondRefundRows[0] ||
      stockRefundRows[0] ||
      null;
    if (fallbackRow) {
      customer = {
        email: fallbackRow.email,
        register: fallbackRow.register,
        lastName: fallbackRow.lastName,
        firstName: fallbackRow.firstName,
        phone: fallbackRow.phone,
      };
    }
  }

  return {
    customer,
    hudaldanAvalt,
    tseneglelt: tsenegleltRows,
    bondRefunds: bondRefundRows,
    stockRefunds: stockRefundRows,
    holdingKeys: purchaseResult.holdingKeys,
  };
}

export function useHuulga() {
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [yearsData, setYearsData] = useState<YearData[]>([]);
  const [yearFileInfos, setYearFileInfos] = useState<YearFileInfo[]>([]);
  const [yearQuarterData, setYearQuarterData] = useState<
    YearQuarterData[]
  >([]);
  const [yearQuarterFileInfos, setYearQuarterFileInfos] =
    useState<YearQuarterFileInfo[]>([]);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [quarterResult, setQuarterResult] =
    useState<QuarterSearchResult | null>(null);
  const [error, setError] = useState<string>("");
  const [selectedUploadYear, setSelectedUploadYear] = useState<number>(2024);
  const [selectedUploadQuarter, setSelectedUploadQuarter] =
    useState<HuulgaQuarter>("Q1");
  const [filterMode, setFilterMode] = useState<"total" | "yearly">("total");
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedQuarterKeys, setSelectedQuarterKeys] =
    useState<string[]>([]);

  // Available years derived from loaded data
  const availableYears = useMemo(
    () => [...new Set(yearsData.map((y) => y.year))].sort(),
    [yearsData]
  );

  const availableQuarterYears = useMemo(
    () =>
      [
        ...new Set(
          yearQuarterData.map(
            (item) => item.year
          )
        ),
      ].sort(),
    [yearQuarterData]
  );

  const availableYearQuarterKeys = useMemo(
    () =>
      yearQuarterData.map(
        (item) => `${item.year}-${item.quarter}`
      ),
    [yearQuarterData]
  );

  // Load all files from backend on mount
  useEffect(() => {
    const loadFromBackend = async () => {
      try {
        // Get file list (without csvContent)
        const fileList = await apiFetch("/files");
        if (!Array.isArray(fileList) || fileList.length === 0) {
          setDataLoading(false);
          return;
        }

        // Group by year
        const yearMap = new Map<number, YearData>();
        const yearQuarterMap = new Map<
          string,
          YearQuarterData
        >();
        const infos: YearFileInfo[] = [];
        const quarterInfos: YearQuarterFileInfo[] = [];

        for (const f of fileList) {
          const legacyField = API_TYPE_TO_FIELD[f.fileType];
          const quarterField =
            API_TYPE_TO_QUARTER_FIELD[f.fileType];
          const quarter =
            HUULGA_QUARTERS.includes(f.quarter)
              ? f.quarter
              : "Q1";

          if (legacyField) {
            infos.push({
              year: f.year,
              fileType: legacyField,
              fileName: f.fileName,
              updatedAt: f.updatedAt,
            });

            if (!yearMap.has(f.year)) {
              yearMap.set(f.year, {
                year: f.year,
                haNiitText: "",
                tseneglelText: "",
                zarlagaText: "",
                buitsaaltText: "",
              });
            }
          }

          if (quarterField) {
            quarterInfos.push({
              year: f.year,
              quarter,
              fileType: quarterField,
              fileName: f.fileName,
              updatedAt: f.updatedAt,
            });

            const key = `${f.year}-${quarter}`;
            if (!yearQuarterMap.has(key)) {
              yearQuarterMap.set(key, {
                year: f.year,
                quarter,
                niitHudaldanAvaltText: "",
                niitTsenegleltText: "",
                buitsaaltBondText: "",
                buitsaaltStockText: "",
              });
            }
          }
        }

        // Fetch each file's csvContent
        const downloads = fileList.map((f: any) => {
          const quarterQuery = f.quarter
            ? `&quarter=${encodeURIComponent(f.quarter)}`
            : "";
          return apiFetch(
            `/download?year=${f.year}&fileType=${f.fileType}${quarterQuery}`
          );
        });
        const results = await Promise.all(downloads);

        for (const r of results) {
          const yd = yearMap.get(r.year);
          const legacyField = API_TYPE_TO_FIELD[r.fileType];
          if (yd && legacyField) {
            (yd as any)[legacyField] = r.csvContent || "";
          }

          const quarterField =
            API_TYPE_TO_QUARTER_FIELD[r.fileType];
          const quarter =
            HUULGA_QUARTERS.includes(r.quarter)
              ? r.quarter
              : "Q1";
          const quarterData = yearQuarterMap.get(
            `${r.year}-${quarter}`
          );
          if (quarterData && quarterField) {
            (quarterData as any)[quarterField] =
              r.csvContent || "";
          }
        }

        const allYears = [...yearMap.values()].sort((a, b) => a.year - b.year);
        const allYearQuarters = [
          ...yearQuarterMap.values(),
        ].sort((a, b) =>
          a.year === b.year
            ? HUULGA_QUARTERS.indexOf(a.quarter) -
              HUULGA_QUARTERS.indexOf(b.quarter)
            : a.year - b.year
        );
        setYearsData(allYears);
        setYearFileInfos(infos);
        setYearQuarterData(allYearQuarters);
        setYearQuarterFileInfos(quarterInfos);
        setSelectedYears(allYears.map((y) => y.year));
        setSelectedQuarterKeys(
          allYearQuarters.map(
            (item) => `${item.year}-${item.quarter}`
          )
        );
      } catch (e) {
        console.error("Failed to load from backend:", e);
      } finally {
        setDataLoading(false);
      }
    };
    loadFromBackend();
  }, []);

  // Upload file for a specific year (saves to backend + local state)
  const handleFileUpload = useCallback(
    async (fileType: FileTypeKey, file: File, year: number) => {
      const csvText = await fileToCSV(file);
      if (!csvText.trim()) {
        throw new Error("Файл хоосон байна");
      }
      const apiFileType = FILE_TYPE_MAP[fileType];

      // Save to backend
      try {
        const byteLength = getUtf8ByteLength(csvText);
        if (byteLength > PLAIN_UPLOAD_MAX_BYTES) {
          await uploadChunkedCsv({
            year,
            fileType: apiFileType,
            fileName: file.name,
            csvText,
          });
        } else {
          await apiFetch("/upload", {
            method: "POST",
            body: JSON.stringify({
              year,
              fileType: apiFileType,
              csvContent: csvText,
              fileName: file.name,
            }),
          });
        }
      } catch (e) {
        console.error("Failed to save to backend:", e);
      }

      // Update local state
      setYearsData((prev) => {
        const existing = prev.find((y) => y.year === year);
        if (existing) {
          return prev.map((y) =>
            y.year === year ? { ...y, [fileType]: csvText } : y
          );
        }
        const newYear: YearData = {
          year,
          haNiitText: "",
          tseneglelText: "",
          zarlagaText: "",
          buitsaaltText: "",
          [fileType]: csvText,
        };
        return [...prev, newYear].sort((a, b) => a.year - b.year);
      });

      setYearFileInfos((prev) => {
        const filtered = prev.filter(
          (f) => !(f.year === year && f.fileType === fileType)
        );
        return [
          ...filtered,
          { year, fileType, fileName: file.name, updatedAt: new Date().toISOString() },
        ];
      });

      // Auto-add year to selected if not there
      setSelectedYears((prev) =>
        prev.includes(year) ? prev : [...prev, year].sort()
      );
    },
    []
  );

  const handleQuarterFileUpload = useCallback(
    async (
      fileType: QuarterFileTypeKey,
      file: File,
      year: number,
      quarter: HuulgaQuarter,
      onProgress?: (statusText: string) => void
    ) => {
      try {
        onProgress?.("Uploading");
        console.info("[huulga-upload:start]", {
          label: fileType,
          year,
          quarter,
          fileName: file.name,
          size: file.size,
        });
        const csvText = await fileToCSV(file);
        if (!csvText.trim()) {
          throw new Error("Файл хоосон байна");
        }
        const apiFileType = QUARTER_FILE_TYPE_MAP[fileType];
        const byteLength = getUtf8ByteLength(csvText);
        console.info("[huulga-upload:parsed]", {
          label: fileType,
          year,
          quarter,
          fileName: file.name,
          byteLength,
        });
        onProgress?.("Uploading");
        console.info("[huulga-upload]", {
          label: fileType,
          year,
          quarter,
          fileName: file.name,
          byteLength,
          mode:
            byteLength > PLAIN_UPLOAD_MAX_BYTES
              ? "chunked"
              : "plain",
        });
        if (byteLength > PLAIN_UPLOAD_MAX_BYTES) {
          await uploadChunkedCsv({
            year,
            quarter,
            fileType: apiFileType,
            fileName: file.name,
            csvText,
            onProgress,
          });
        } else {
          await apiFetch("/upload", {
            method: "POST",
            body: JSON.stringify({
              year,
              quarter,
              fileType: apiFileType,
              csvContent: csvText,
              fileName: file.name,
            }),
          });
        }

        setYearQuarterData((prev) => {
          const key = `${year}-${quarter}`;
          const existing = prev.find(
            (item) => `${item.year}-${item.quarter}` === key
          );

          if (existing) {
            return prev.map((item) =>
              `${item.year}-${item.quarter}` === key
                ? { ...item, [fileType]: csvText }
                : item
            );
          }

          const nextItem: YearQuarterData = {
            year,
            quarter,
            niitHudaldanAvaltText: "",
            niitTsenegleltText: "",
            buitsaaltBondText: "",
            buitsaaltStockText: "",
            [fileType]: csvText,
          };

          return [...prev, nextItem].sort((a, b) =>
            a.year === b.year
              ? HUULGA_QUARTERS.indexOf(a.quarter) -
                HUULGA_QUARTERS.indexOf(b.quarter)
              : a.year - b.year
          );
        });

        setYearQuarterFileInfos((prev) => {
          const filtered = prev.filter(
            (item) =>
              !(
                item.year === year &&
                item.quarter === quarter &&
                item.fileType === fileType
              )
          );
          return [
            ...filtered,
            {
              year,
              quarter,
              fileType,
              fileName: file.name,
              updatedAt: new Date().toISOString(),
            },
          ];
        });

        setSelectedQuarterKeys((prev) => {
          const nextKey = `${year}-${quarter}`;
          return prev.includes(nextKey)
            ? prev
            : [...prev, nextKey];
        });

        return { ok: true as const };
      } catch (e) {
        console.error("Failed to save quarter file to backend:", e);
        const message =
          e instanceof HuulgaApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : "Файл боловсруулах эсвэл backend дээр хадгалахад алдаа гарлаа";
        const status =
          e instanceof HuulgaApiError ? e.status : undefined;
        return { ok: false as const, message, status };
      }
    },
    []
  );

  const deleteYearQuarterFiles = useCallback(
    async (year: number, quarter: HuulgaQuarter) => {
      const fileTypes = Object.entries(
        QUARTER_FILE_TYPE_MAP
      ) as [QuarterFileTypeKey, string][];

      await Promise.all(
        fileTypes.map(([, apiFileType]) =>
          apiDelete("/file", {
            year,
            quarter,
            fileType: apiFileType,
          })
        )
      );

      setYearQuarterData((prev) =>
        prev.map((item) =>
          item.year === year && item.quarter === quarter
            ? {
                ...item,
                niitHudaldanAvaltText: "",
                niitTsenegleltText: "",
                buitsaaltBondText: "",
                buitsaaltStockText: "",
              }
            : item
        )
      );

      setYearQuarterFileInfos((prev) =>
        prev.filter(
          (item) => !(item.year === year && item.quarter === quarter)
        )
      );
    },
    []
  );

  const isFileUploaded = useCallback(
    (year: number, fileType: string): boolean => {
      // Check yearFileInfos for user uploads
      if (yearFileInfos.some((f) => f.year === year && f.fileType === fileType)) {
        return true;
      }
      // Check if default 2024 data loaded
      if (year === 2024) {
        const yd = yearsData.find((y) => y.year === 2024);
        if (!yd) return false;
        const fieldMap: Record<string, keyof YearData> = {
          haNiitText: "haNiitText",
          tseneglelText: "tseneglelText",
          zarlagaText: "zarlagaText",
          buitsaaltText: "buitsaaltText",
        };
        const field = fieldMap[fileType];
        return field ? !!(yd[field] as string) : false;
      }
      return false;
    },
    [yearFileInfos, yearsData]
  );

  const isQuarterFileUploaded = useCallback(
    (
      year: number,
      quarter: HuulgaQuarter,
      fileType: QuarterFileTypeKey
    ): boolean =>
      yearQuarterFileInfos.some(
        (item) =>
          item.year === year &&
          item.quarter === quarter &&
          item.fileType === fileType
      ) ||
      yearQuarterData.some(
        (item) =>
          item.year === year &&
          item.quarter === quarter &&
          !!item[fileType]
      ),
    [yearQuarterData, yearQuarterFileInfos]
  );

  // Add a new year
  const addYear = useCallback((year: number) => {
    setYearsData((prev) => {
      if (prev.some((y) => y.year === year)) return prev;
      return [
        ...prev,
        { year, haNiitText: "", tseneglelText: "", zarlagaText: "", buitsaaltText: "" },
      ].sort((a, b) => a.year - b.year);
    });
    setSelectedUploadYear(year);
  }, []);

  const addYearQuarter = useCallback(
    (year: number, quarter: HuulgaQuarter) => {
      setYearQuarterData((prev) => {
        if (
          prev.some(
            (item) =>
              item.year === year &&
              item.quarter === quarter
          )
        ) {
          return prev;
        }

        return [
          ...prev,
          {
            year,
            quarter,
            niitHudaldanAvaltText: "",
            niitTsenegleltText: "",
            buitsaaltBondText: "",
            buitsaaltStockText: "",
          },
        ].sort((a, b) =>
          a.year === b.year
            ? HUULGA_QUARTERS.indexOf(a.quarter) -
              HUULGA_QUARTERS.indexOf(b.quarter)
            : a.year - b.year
        );
      });
      setSelectedUploadYear(year);
      setSelectedUploadQuarter(quarter);
    },
    []
  );

  // Get years to search based on filter mode
  const getFilteredYears = useCallback((): YearData[] => {
    if (filterMode === "total") return yearsData;
    return yearsData.filter((y) => selectedYears.includes(y.year));
  }, [filterMode, yearsData, selectedYears]);

  const getFilteredYearQuarters = useCallback((): YearQuarterData[] => {
    if (filterMode === "total") return yearQuarterData;
    return yearQuarterData.filter((item) =>
      selectedQuarterKeys.includes(
        `${item.year}-${item.quarter}`
      )
    );
  }, [filterMode, yearQuarterData, selectedQuarterKeys]);

  const search = useCallback(
    (email: string) => {
      setLoading(true);
      setError("");
      setResult(null);

      try {
        const emailLower = email.toLowerCase().trim();
        if (!emailLower) {
          setError("Имейл хаяг оруулна уу");
          setLoading(false);
          return;
        }

        const filteredYears = getFilteredYears();
        if (filteredYears.length === 0) {
          setError("Он сонгоогүй байна");
          setLoading(false);
          return;
        }

        let customer: CustomerInfo | null = null;
        const allOZ: OrlogoZarlagaRow[] = [];
        const allHA: HudaldanAvaltRow[] = [];
        const allBU: BuitsaaltRow[] = [];

        for (const yd of filteredYears) {
          const res = searchYear(yd, emailLower);
          if (res.customer && !customer) customer = res.customer;
          allOZ.push(...res.orlogoZarlaga);
          allHA.push(...res.hudaldanAvalt);
          allBU.push(...res.buitsaalt);
        }

        if (!customer) {
          setError("Мэдээлэл олдсонгүй");
          setLoading(false);
          return;
        }

        setResult({
          customer,
          orlogoZarlaga: allOZ,
          hudaldanAvalt: allHA,
          buitsaalt: allBU,
        });
      } catch (e) {
        console.error("Search error:", e);
        setError("Хайлтын алдаа гарлаа");
      } finally {
        setLoading(false);
      }
    },
    [getFilteredYears]
  );

  const searchQuarter = useCallback(
    (email: string) => {
      setLoading(true);
      setError("");
      setResult(null);
      setQuarterResult(null);

      try {
        const emailLower = email.toLowerCase().trim();
        if (!emailLower) {
          setError("Имейл хаяг оруулна уу");
          setLoading(false);
          return;
        }

        const filtered = getFilteredYearQuarters();
        if (filtered.length === 0) {
          setError("Улирал сонгоогүй байна");
          setLoading(false);
          return;
        }

        let customer: CustomerInfo | null = null;
        const hudaldanAvalt: QuarterHudaldanAvaltRow[] = [];
        const tseneglelt: TsenegleltRow[] = [];
        const bondRefunds: BondRefundRow[] = [];
        const stockRefunds: StockRefundRow[] = [];
        const holdingKeySet = new Set<string>();

        for (const item of filtered) {
          const res = searchYearQuarter(item, emailLower);
          if (res.customer && !customer) {
            customer = res.customer;
          }
          res.holdingKeys.forEach((key) =>
            holdingKeySet.add(key)
          );
          hudaldanAvalt.push(...res.hudaldanAvalt);
          tseneglelt.push(...res.tseneglelt);
          bondRefunds.push(...res.bondRefunds);
          stockRefunds.push(...res.stockRefunds);
        }

        if (!customer) {
          setError("Мэдээлэл олдсонгүй");
          setLoading(false);
          return;
        }

        setQuarterResult({
          customer,
          hudaldanAvalt,
          tseneglelt,
          bondRefunds,
          stockRefunds,
          holdingKeys: [...holdingKeySet],
        });
      } catch (e) {
        console.error("Quarter search error:", e);
        setError("Хайлтын алдаа гарлаа");
      } finally {
        setLoading(false);
      }
    },
    [getFilteredYearQuarters]
  );

  const downloadExcel = useCallback(async () => {
    if (quarterResult) {
      const workbook = new ExcelJS.Workbook();

      const thinBorder: Partial<ExcelJS.Borders> = {
        top: { style: "thin" }, left: { style: "thin" },
        bottom: { style: "thin" }, right: { style: "thin" },
      };
      const headerFill: ExcelJS.FillPattern = {
        type: "pattern", pattern: "solid", fgColor: { argb: "FF1F3864" },
      };
      const headerFont: Partial<ExcelJS.Font> = {
        bold: true,
        color: { argb: "FFFFFFFF" },
        size: 10,
      };
      const labelFont: Partial<ExcelJS.Font> = { bold: true, size: 10 };
      const valueFont: Partial<ExcelJS.Font> = {
        bold: true,
        color: { argb: "FF0070C0" },
        size: 10,
      };
      const dataFont: Partial<ExcelJS.Font> = { size: 10 };
      const sectionFont: Partial<ExcelJS.Font> = {
        bold: true,
        size: 11,
        color: { argb: "FF1F3864" },
      };

      const addCustomerHeader = (ws: ExcelJS.Worksheet) => {
        ws.addRow([]);
        const titleRow = ws.addRow([]);
        titleRow.getCell(1).value = "Харилцагчийн мэдээлэл";
        titleRow.getCell(1).font = { bold: true, size: 11 };
        titleRow.getCell(1).border = thinBorder;
        titleRow.getCell(2).border = thinBorder;

        const infoItems: [string, string][] = [
          ["Имейл", quarterResult.customer.email],
          ["Регистр", quarterResult.customer.register],
          ["Овог", quarterResult.customer.lastName],
          ["Нэр", quarterResult.customer.firstName],
          ["Утасны дугаар", quarterResult.customer.phone],
        ];

        infoItems.forEach(([label, value]) => {
          const row = ws.addRow([]);
          row.getCell(1).value = label;
          row.getCell(1).font = labelFont;
          row.getCell(1).border = thinBorder;
          row.getCell(2).value = value;
          row.getCell(2).font = valueFont;
          row.getCell(2).border = thinBorder;
        });
        ws.addRow([]);
      };

      const addHeaderRow = (ws: ExcelJS.Worksheet, headers: string[]) => {
        const row = ws.addRow([]);
        row.height = 32;
        headers.forEach((header, idx) => {
          const cell = row.getCell(idx + 1);
          cell.value = header;
          cell.font = headerFont;
          cell.fill = headerFill;
          cell.border = thinBorder;
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
        });
      };

      const addDataRow = (
        ws: ExcelJS.Worksheet,
        values: Array<string | number>
      ) => {
        const row = ws.addRow([]);
        values.forEach((value, idx) => {
          const cell = row.getCell(idx + 1);
          cell.value = value;
          cell.font = dataFont;
          cell.border = thinBorder;
          cell.alignment = { vertical: "middle" };
        });
      };

      const addSectionTitle = (ws: ExcelJS.Worksheet, title: string) => {
        const row = ws.addRow([]);
        row.getCell(1).value = title;
        row.getCell(1).font = sectionFont;
      };

      const sortedPurchases = [...quarterResult.hudaldanAvalt].sort((a, b) =>
        `${a.year}-${a.quarter}-${a.dateTime}`.localeCompare(
          `${b.year}-${b.quarter}-${b.dateTime}`
        )
      );
      const sortedDeposits = [...quarterResult.tseneglelt].sort((a, b) =>
        `${a.year}-${a.quarter}-${a.date}`.localeCompare(
          `${b.year}-${b.quarter}-${b.date}`
        )
      );
      const sortedBondRefunds = [...quarterResult.bondRefunds].sort((a, b) =>
        `${a.year}-${a.quarter}-${a.calcDate}`.localeCompare(
          `${b.year}-${b.quarter}-${b.calcDate}`
        )
      );
      const sortedStockRefunds = [...quarterResult.stockRefunds].sort((a, b) =>
        `${a.year}-${a.quarter}-${a.purchaseDate}`.localeCompare(
          `${b.year}-${b.quarter}-${b.purchaseDate}`
        )
      );

      const purchaseSheet = workbook.addWorksheet("Худалдан авалт");
      const purchaseHeaders = [
        "Он",
        "Улирал",
        "Огноо",
        "Огноо/Цаг",
        "Мөнгөн дүн",
        "Багц №",
        "Бондын нэр",
        "Bond",
        "Битүүмжлэгдсэн дүн",
        ...quarterResult.holdingKeys,
      ];
      purchaseHeaders.forEach((_, idx) => {
        purchaseSheet.getColumn(idx + 1).width =
          idx < 4 ? 16 : idx < 9 ? 14 : 12;
      });
      addCustomerHeader(purchaseSheet);
      addHeaderRow(purchaseSheet, purchaseHeaders);
      sortedPurchases.forEach((row) => {
        addDataRow(purchaseSheet, [
          row.year,
          row.quarter,
          row.date,
          row.dateTime,
          row.amount,
          row.batchNo,
          row.bondName,
          row.bond,
          row.lockAmount,
          ...quarterResult.holdingKeys.map(
            (holdingKey) => row.holdings[holdingKey] || ""
          ),
        ]);
      });

      const depositSheet = workbook.addWorksheet("Цэнэглэлт");
      const depositHeaders = [
        "Он",
        "Улирал",
        "Огноо",
        "Дүн",
        "Зориулалт",
        "Тайлбар",
        "Цэнэглэсэн огноо / Данс",
      ];
      [10, 10, 18, 14, 18, 30, 22].forEach((width, idx) => {
        depositSheet.getColumn(idx + 1).width = width;
      });
      addCustomerHeader(depositSheet);
      addHeaderRow(depositSheet, depositHeaders);
      sortedDeposits.forEach((row) => {
        addDataRow(depositSheet, [
          row.year,
          row.quarter,
          row.date,
          row.amount,
          row.purpose,
          row.description,
          row.depositDate,
        ]);
      });

      const refundSheet = workbook.addWorksheet("Буцаалт");
      [10, 10, 16, 18, 14, 12, 16, 12, 14, 14, 14, 16, 16, 12, 14, 16].forEach(
        (width, idx) => {
          refundSheet.getColumn(idx + 1).width = width;
        }
      );
      addCustomerHeader(refundSheet);

      addSectionTitle(refundSheet, "Буцаалт (Бонд)");
      addHeaderRow(refundSheet, [
        "Он",
        "Улирал",
        "Тооцоолсон огноо",
        "Хөрөнгийн нэр",
        "Үндсэн дүн",
        "Хүү %",
        "Худалдан авсан огноо",
        "Хоног",
        "Тооцсон хүү",
        "30% хүү",
        "Нийт",
        "AvtaMata шимтгэл",
        "Арилжааны шимтгэл",
        "ОАТ",
        "Шилжүүлэх дүн",
        "Данс",
      ]);
      sortedBondRefunds.forEach((row) => {
        addDataRow(refundSheet, [
          row.year,
          row.quarter,
          row.calcDate,
          row.assetName,
          row.principal,
          row.bondInterestRate,
          row.purchaseDate,
          row.days,
          row.calcInterest,
          row.interestThirtyPercent,
          row.total,
          row.avtamataFee,
          row.tradingFee,
          row.tax,
          row.transferAmount,
          row.account,
        ]);
      });

      refundSheet.addRow([]);
      addSectionTitle(refundSheet, "Буцаалт (Хувьцаа)");
      addHeaderRow(refundSheet, [
        "Он",
        "Улирал",
        "Худалдан авсан огноо",
        "Хөрөнгийн нэр",
        "Тоо ширхэг",
        "Ширхэгийн үнэ",
        "USD rate",
        "Нийт",
        "Арилжааны шимтгэл",
        "AvtaMata шимтгэл",
        "Шилжүүлэх дүн",
        "Данс",
      ]);
      sortedStockRefunds.forEach((row) => {
        addDataRow(refundSheet, [
          row.year,
          row.quarter,
          row.purchaseDate,
          row.stockName,
          row.quantity,
          row.stockPrice,
          row.usdRate,
          row.grossTotal,
          row.tradingFee,
          row.avtamataFee,
          row.transferAmount,
          row.account,
        ]);
      });

      const quarterBuffer = await workbook.xlsx.writeBuffer();
      const quarterBlob = new Blob([quarterBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const quarterUrl = window.URL.createObjectURL(quarterBlob);
      const quarterLink = document.createElement("a");
      quarterLink.href = quarterUrl;
      quarterLink.download = `Хуулга_${quarterResult.customer.email}.xlsx`;
      quarterLink.click();
      window.URL.revokeObjectURL(quarterUrl);
      return;
    }

    if (!result) return;

    const workbook = new ExcelJS.Workbook();

    const thinBorder: Partial<ExcelJS.Borders> = {
      top: { style: "thin" }, left: { style: "thin" },
      bottom: { style: "thin" }, right: { style: "thin" },
    };
    const headerFill: ExcelJS.FillPattern = {
      type: "pattern", pattern: "solid", fgColor: { argb: "FF1F3864" },
    };
    const headerFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
    const labelFont: Partial<ExcelJS.Font> = { bold: true, size: 10 };
    const valueFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: "FF0070C0" }, size: 10 };
    const dataFont: Partial<ExcelJS.Font> = { size: 10 };
    const yearFont: Partial<ExcelJS.Font> = { bold: true, size: 11, color: { argb: "FF1F3864" } };
    const yearFill: ExcelJS.FillPattern = {
      type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EDF5" },
    };

    function addCustomerHeader(ws: ExcelJS.Worksheet) {
      ws.addRow([]);
      const titleRow = ws.addRow([]);
      titleRow.getCell(1).value = "Харилцагчийн мэдээлэл";
      titleRow.getCell(1).font = { bold: true, size: 11 };
      titleRow.getCell(1).border = thinBorder;
      titleRow.getCell(2).border = thinBorder;

      const infoItems: [string, string][] = [
        ["Имейл", result!.customer.email],
        ["Регистр", result!.customer.register],
        ["Овог", result!.customer.lastName],
        ["Нэр", result!.customer.firstName],
        ["Утасны дугаар", result!.customer.phone],
      ];
      infoItems.forEach(([label, value]) => {
        const row = ws.addRow([]);
        row.getCell(1).value = label;
        row.getCell(1).font = labelFont;
        row.getCell(1).border = thinBorder;
        row.getCell(2).value = value;
        row.getCell(2).font = valueFont;
        row.getCell(2).border = thinBorder;
      });
      ws.addRow([]);
    }

    function addHeaderRow(ws: ExcelJS.Worksheet, headers: string[]) {
      const row = ws.addRow([]);
      row.height = 32;
      headers.forEach((h, idx) => {
        const cell = row.getCell(idx + 1);
        cell.value = h;
        cell.font = headerFont;
        cell.fill = headerFill;
        cell.border = thinBorder;
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      });
    }

    function addYearRow(ws: ExcelJS.Worksheet, year: number, colCount: number) {
      const row = ws.addRow([]);
      for (let c = 1; c <= colCount; c++) {
        const cell = row.getCell(c);
        cell.fill = yearFill;
        cell.border = thinBorder;
        if (c === 1) {
          cell.value = `${year} он`;
          cell.font = yearFont;
        }
      }
    }

    function addDataRow(ws: ExcelJS.Worksheet, values: string[]) {
      const row = ws.addRow([]);
      values.forEach((v, idx) => {
        const cell = row.getCell(idx + 1);
        cell.value = v;
        cell.font = dataFont;
        cell.border = thinBorder;
        cell.alignment = { vertical: "middle" };
      });
    }

    // Get unique years from data
    const getYears = (rows: { year: number }[]) =>
      [...new Set(rows.map((r) => r.year))].sort();

    // Sheet 1: Орлого зарлага
    const ws1 = workbook.addWorksheet("Орлого зарлага");
    [22, 16, 16, 36, 22].forEach((w, i) => { ws1.getColumn(i + 1).width = w; });
    addCustomerHeader(ws1);
    const ozHeaders = ["Огноо", "Орлого", "Зарлага", "Тайлбар", "Цэнэглэсэн огноо / Данс"];
    addHeaderRow(ws1, ozHeaders);
    getYears(result.orlogoZarlaga).forEach((year) => {
      addYearRow(ws1, year, 5);
      result.orlogoZarlaga
        .filter((r) => r.year === year)
        .forEach((oz) => addDataRow(ws1, [oz.date, oz.income, oz.expense, oz.description, oz.depositDate]));
    });

    // Sheet 2: Худалдан авалт
    const ws2 = workbook.addWorksheet("Худалдан авалт");
    [20, 16, 10, 16, 12, 20, 12, 12, 12, 12, 12].forEach((w, i) => { ws2.getColumn(i + 1).width = w; });
    addCustomerHeader(ws2);
    const haHeaders = ["Огноо", "Мөнгөн дүн", "Багц №", "Bond", "Estate", "Худалдан авалтаас үлдсэн дүн", "FX", "DIA", "VDE", "VUG", "VXF"];
    addHeaderRow(ws2, haHeaders);
    getYears(result.hudaldanAvalt).forEach((year) => {
      addYearRow(ws2, year, 11);
      result.hudaldanAvalt
        .filter((r) => r.year === year)
        .forEach((ha) => addDataRow(ws2, [ha.date, ha.amount, ha.batchNo, ha.bond, ha.estate, ha.lockAmount, ha.fx, ha.dia, ha.vde, ha.vug, ha.vxf]));
    });

    // Sheet 3: Буцаалт
    const ws3 = workbook.addWorksheet("Буцаалт");
    [18, 16, 16, 12, 16, 14, 16, 18, 20, 10, 16, 14].forEach((w, i) => { ws3.getColumn(i + 1).width = w; });
    addCustomerHeader(ws3);
    const buHeaders = ["Тооцоолсон огноо", "Хөрөнгийн нэр", "Үндсэн дүн", "Бонд хүү", "Хүү тооцох хоног", "Тооцсон хүү", "Нийт", "AvtaMata шимтгэл", "Арилжааны шимтгэл", "ОАТ", "Шилжүүлэх дүн", "Данс"];
    addHeaderRow(ws3, buHeaders);
    getYears(result.buitsaalt).forEach((year) => {
      addYearRow(ws3, year, 12);
      result.buitsaalt
        .filter((r) => r.year === year)
        .forEach((bu) => addDataRow(ws3, [bu.calcDate, bu.assetName, bu.principal, bu.bondInterest, bu.days, bu.calcInterest, bu.total, bu.avtamataFee, bu.tradingFee, bu.tax, bu.transferAmount, bu.account]));
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Хуулга_${result.customer.email}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [quarterResult, result]);

  const downloadPDF = useCallback(async () => {
    if (quarterResult) {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

      const [regularBuf, boldBuf] = await Promise.all([
        fetch("/Roboto-Regular.ttf").then((r) => r.arrayBuffer()),
        fetch("/Roboto-Bold.ttf").then((r) => r.arrayBuffer()),
      ]);

      const toBase64 = (buf: ArrayBuffer) => {
        const bytes = new Uint8Array(buf);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary);
      };

      doc.addFileToVFS("Roboto-Regular.ttf", toBase64(regularBuf));
      doc.addFileToVFS("Roboto-Bold.ttf", toBase64(boldBuf));
      doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
      doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");

      doc.setFont("Roboto", "bold");
      doc.setFontSize(14);
      doc.text("Хуулга", 14, 15);

      doc.setFont("Roboto", "normal");
      doc.setFontSize(10);
      const info = [
        `Имейл: ${quarterResult.customer.email}`,
        `Регистр: ${quarterResult.customer.register}`,
        `Овог: ${quarterResult.customer.lastName}    Нэр: ${quarterResult.customer.firstName}    Утас: ${quarterResult.customer.phone}`,
      ];
      info.forEach((line, i) => doc.text(line, 14, 23 + i * 5));

      let startY = 40;
      const headStyle = { fillColor: [31, 56, 100] as [number, number, number], textColor: 255 as const, fontStyle: "bold" as const, font: "Roboto" };
      const bodyStyle = { fontSize: 8, cellPadding: 1.5, font: "Roboto" };

      doc.setFont("Roboto", "bold");
      doc.setFontSize(12);
      doc.text("Худалдан авалт", 14, startY);
      autoTable(doc, {
        startY: startY + 2,
        head: [[
          "Он",
          "Улирал",
          "Огноо",
          "Мөнгөн дүн",
          "Бондын нэр",
          "Bond",
          "Битүүмжлэгдсэн дүн",
          "Holding-ууд",
        ]],
        body: quarterResult.hudaldanAvalt.map((row) => [
          row.year,
          row.quarter,
          row.date,
          row.amount,
          row.bondName,
          row.bond,
          row.lockAmount,
          quarterResult.holdingKeys
            .map((holdingKey) =>
              row.holdings[holdingKey]
                ? `${holdingKey}: ${row.holdings[holdingKey]}`
                : ""
            )
            .filter(Boolean)
            .join(", "),
        ]),
        styles: { ...bodyStyle, fontSize: 7 },
        headStyles: headStyle,
        theme: "grid",
        margin: { left: 14, right: 14 },
      });

      startY = (doc as any).lastAutoTable.finalY + 10;
      if (startY > 170) { doc.addPage(); startY = 15; }
      doc.setFont("Roboto", "bold");
      doc.setFontSize(12);
      doc.text("Цэнэглэлт", 14, startY);
      autoTable(doc, {
        startY: startY + 2,
        head: [["Он", "Улирал", "Огноо", "Дүн", "Зориулалт", "Тайлбар", "Цэнэглэсэн огноо / Данс"]],
        body: quarterResult.tseneglelt.map((row) => [
          row.year,
          row.quarter,
          row.date,
          row.amount,
          row.purpose,
          row.description,
          row.depositDate,
        ]),
        styles: bodyStyle,
        headStyles: headStyle,
        theme: "grid",
        margin: { left: 14, right: 14 },
      });

      startY = (doc as any).lastAutoTable.finalY + 10;
      if (startY > 170) { doc.addPage(); startY = 15; }
      doc.setFont("Roboto", "bold");
      doc.setFontSize(12);
      doc.text("Буцаалт (Бонд)", 14, startY);
      autoTable(doc, {
        startY: startY + 2,
        head: [["Он", "Улирал", "Тооцоолсон огноо", "Хөрөнгийн нэр", "Үндсэн дүн", "Хүү %", "Хоног", "Тооцсон хүү", "Нийт", "Шилжүүлэх дүн"]],
        body: quarterResult.bondRefunds.map((row) => [
          row.year,
          row.quarter,
          row.calcDate,
          row.assetName,
          row.principal,
          row.bondInterestRate,
          row.days,
          row.calcInterest,
          row.total,
          row.transferAmount,
        ]),
        styles: { ...bodyStyle, fontSize: 7 },
        headStyles: headStyle,
        theme: "grid",
        margin: { left: 14, right: 14 },
      });

      startY = (doc as any).lastAutoTable.finalY + 10;
      if (startY > 170) { doc.addPage(); startY = 15; }
      doc.setFont("Roboto", "bold");
      doc.setFontSize(12);
      doc.text("Буцаалт (Хувьцаа)", 14, startY);
      autoTable(doc, {
        startY: startY + 2,
        head: [["Он", "Улирал", "Худалдан авсан огноо", "Хөрөнгийн нэр", "Тоо ширхэг", "Ширхэгийн үнэ", "Нийт", "Шилжүүлэх дүн"]],
        body: quarterResult.stockRefunds.map((row) => [
          row.year,
          row.quarter,
          row.purchaseDate,
          row.stockName,
          row.quantity,
          row.stockPrice,
          row.grossTotal,
          row.transferAmount,
        ]),
        styles: { ...bodyStyle, fontSize: 7 },
        headStyles: headStyle,
        theme: "grid",
        margin: { left: 14, right: 14 },
      });

      doc.save(`Хуулга_${quarterResult.customer.email}.pdf`);
      return;
    }

    if (!result) return;

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    // Load Roboto font for Cyrillic support
    const [regularBuf, boldBuf] = await Promise.all([
      fetch("/Roboto-Regular.ttf").then((r) => r.arrayBuffer()),
      fetch("/Roboto-Bold.ttf").then((r) => r.arrayBuffer()),
    ]);

    const toBase64 = (buf: ArrayBuffer) => {
      const bytes = new Uint8Array(buf);
      let binary = "";
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return btoa(binary);
    };

    doc.addFileToVFS("Roboto-Regular.ttf", toBase64(regularBuf));
    doc.addFileToVFS("Roboto-Bold.ttf", toBase64(boldBuf));
    doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
    doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");

    doc.setFont("Roboto", "bold");
    doc.setFontSize(14);
    doc.text("Хуулга", 14, 15);

    doc.setFont("Roboto", "normal");
    doc.setFontSize(10);
    const info = [
      `Имейл: ${result.customer.email}`,
      `Регистр: ${result.customer.register}`,
      `Овог: ${result.customer.lastName}    Нэр: ${result.customer.firstName}    Утас: ${result.customer.phone}`,
    ];
    info.forEach((line, i) => doc.text(line, 14, 23 + i * 5));

    let startY = 40;
    const headStyle = { fillColor: [31, 56, 100] as [number, number, number], textColor: 255 as const, fontStyle: "bold" as const, font: "Roboto" };
    const bodyStyle = { fontSize: 8, cellPadding: 1.5, font: "Roboto" };

    doc.setFont("Roboto", "bold");
    doc.setFontSize(12);
    doc.text("Орлого зарлага", 14, startY);
    autoTable(doc, {
      startY: startY + 2,
      head: [["Огноо", "Орлого", "Зарлага", "Тайлбар", "Цэнэглэсэн огноо / Данс"]],
      body: result.orlogoZarlaga.map((r) => [r.date, r.income, r.expense, r.description, r.depositDate]),
      styles: bodyStyle,
      headStyles: headStyle,
      theme: "grid",
      margin: { left: 14, right: 14 },
    });

    startY = (doc as any).lastAutoTable.finalY + 10;
    if (startY > 170) { doc.addPage(); startY = 15; }
    doc.setFont("Roboto", "bold");
    doc.setFontSize(12);
    doc.text("Худалдан авалт", 14, startY);
    autoTable(doc, {
      startY: startY + 2,
      head: [["Огноо", "Мөнгөн дүн", "Багц №", "Bond", "Estate", "Худалдан авалтаас үлдсэн дүн", "FX", "DIA", "VDE", "VUG", "VXF"]],
      body: result.hudaldanAvalt.map((r) => [r.date, r.amount, r.batchNo, r.bond, r.estate, r.lockAmount, r.fx, r.dia, r.vde, r.vug, r.vxf]),
      styles: { ...bodyStyle, fontSize: 7 },
      headStyles: headStyle,
      theme: "grid",
      margin: { left: 14, right: 14 },
    });

    startY = (doc as any).lastAutoTable.finalY + 10;
    if (startY > 170) { doc.addPage(); startY = 15; }
    doc.setFont("Roboto", "bold");
    doc.setFontSize(12);
    doc.text("Буцаалт", 14, startY);
    autoTable(doc, {
      startY: startY + 2,
      head: [["Тооцоолсон огноо", "Хөрөнгийн нэр", "Үндсэн дүн", "Бонд хүү", "Хоног", "Тооцсон хүү", "Нийт", "AvtaMata", "Арилжааны", "ОАТ", "Шилжүүлэх дүн", "Данс"]],
      body: result.buitsaalt.map((r) => [r.calcDate, r.assetName, r.principal, r.bondInterest, r.days, r.calcInterest, r.total, r.avtamataFee, r.tradingFee, r.tax, r.transferAmount, r.account]),
      styles: { ...bodyStyle, fontSize: 7 },
      headStyles: headStyle,
      theme: "grid",
      margin: { left: 14, right: 14 },
    });

    doc.save(`Хуулга_${result.customer.email}.pdf`);
  }, [quarterResult, result]);

  return {
    loading,
    dataLoading,
    showFileUpload,
    setShowFileUpload,
    result,
    quarterResult,
    error,
    search,
    searchQuarter,
    downloadExcel,
    downloadPDF,
    handleFileUpload,
    handleQuarterFileUpload,
    deleteYearQuarterFiles,
    isFileUploaded,
    isQuarterFileUploaded,
    availableYears,
    availableQuarterYears,
    availableYearQuarterKeys,
    selectedUploadYear,
    setSelectedUploadYear,
    selectedUploadQuarter,
    setSelectedUploadQuarter,
    addYear,
    addYearQuarter,
    filterMode,
    setFilterMode,
    selectedYears,
    setSelectedYears,
    selectedQuarterKeys,
    setSelectedQuarterKeys,
    yearQuarterData,
    yearQuarterFileInfos,
  };
}
